import { supabase } from './supabase';
import type { Database } from './database.types';

// Type aliases for better readability
type EmailLog = Database['public']['Tables']['email_logs']['Row'];
type EmailPreferencesDB = Database['public']['Tables']['email_preferences']['Row'];

export type EmailTemplate = 
  // Subscription emails
  | 'subscription-created'
  | 'payment-successful'
  | 'payment-failed'
  | 'subscription-upgraded'
  | 'subscription-downgraded'
  | 'subscription-cancelled'
  | 'subscription-expiring'
  | 'trial-ending'
  | 'credit-purchase-receipt'
  // Team emails
  | 'team-invitation'
  | 'invitation-accepted'
  | 'team-member-removed'
  | 'role-changed'
  // Project emails
  | 'project-shared'
  | 'analysis-complete'
  | 'project-deadline-reminder'
  | 'weekly-activity-digest'
  // Usage emails
  | 'credits-running-low'
  | 'credits-exhausted'
  | 'storage-limit-warning'
  | 'storage-warning'
  | 'storage-full'
  | 'monthly-usage-report'
  // Support emails
  | 'support-request';

export interface EmailData {
  to: string;
  template: EmailTemplate;
  data: Record<string, any>;
  userId?: string;
  subject?: string;
  attachments?: Array<{
    filename: string;
    content: string | Buffer;
  }>;
  recaptchaToken?: string;
}

export interface EmailPreferences {
  marketing_emails: boolean;
  activity_digest: boolean;
  project_notifications: boolean;
  usage_alerts: boolean;
}

interface EmailFunctionResponse {
  success?: boolean;
  message?: string;
  error?: string;
}

export class EmailService {
  /**
   * Generate email subject based on template
   */
  private static getSubjectForTemplate(template: EmailTemplate, data: Record<string, any>): string {
    const subjects: Record<EmailTemplate, string> = {
      // Analysis emails
      'analysis-complete': `Analysis Complete: ${data.creativeName || 'Your Creative'}`,
      
      // Subscription emails
      'subscription-created': `Welcome to Rulevia ${data.planName || 'Pro'}!`,
      'payment-successful': 'Payment Received - Rulevia',
      'payment-failed': 'Payment Failed - Rulevia',
      'subscription-upgraded': `Upgraded to Rulevia ${data.planName || 'Pro'}!`,
      'subscription-downgraded': `Plan Changed to Rulevia ${data.planName || 'Basic'}`,
      'subscription-cancelled': 'Subscription Cancelled - Rulevia',
      'subscription-expiring': 'Your Rulevia Subscription is Expiring',
      'trial-ending': 'Your Rulevia Trial is Ending',
      'credit-purchase-receipt': 'Credit Purchase Receipt - Rulevia',
      
      // Team emails
      'team-invitation': `You're invited to join ${data.organizationName || 'Team'} on Rulevia`,
      'invitation-accepted': 'Team Invitation Accepted - Rulevia',
      'team-member-removed': 'Team Member Removed - Rulevia',
      'role-changed': 'Your Role Has Changed - Rulevia',
      
      // Project emails
      'project-shared': `Project Shared: ${data.projectName || 'Rulevia Project'}`,
      'project-deadline-reminder': `Project Deadline Reminder: ${data.projectName || 'Your Project'}`,
      'weekly-activity-digest': 'Your Weekly Activity Digest - Rulevia',
      
      // Usage emails
      'credits-running-low': 'Credits Running Low - Rulevia',
      'credits-exhausted': 'Credits Exhausted - Rulevia',
      'storage-limit-warning': 'Storage Limit Warning - Rulevia',
      'storage-warning': 'Storage Warning - Rulevia',
      'storage-full': 'Storage Full - Rulevia',
      'monthly-usage-report': 'Monthly Usage Report - Rulevia',
      
      // Support emails
      'support-request': `Support Request: ${data.subject || 'New Request'}`,
    };

    return subjects[template] || 'Notification from Rulevia';
  }

  /**
   * Send an email using the Supabase edge function
   */
  static async send(params: EmailData): Promise<void> {
    try {
      // Generate subject if not provided
      const subject = params.subject || this.getSubjectForTemplate(params.template, params.data);

      const { data, error } = await supabase.functions.invoke<EmailFunctionResponse>('send-email', {
        body: {
          to: params.to,
          subject: subject,
          template: params.template,
          data: params.data,
          userId: params.userId,
          attachments: params.attachments,
          recaptchaToken: params.recaptchaToken,
        },
      });

      if (error) {
        console.error('Failed to send email:', error);
        throw new Error(`Failed to send email: ${error.message}`);
      }

      if (data?.error) {
        console.error('Email service error:', data.error);
        throw new Error(`Email service error: ${data.error}`);
      }

      console.log('Email sent successfully:', data);
    } catch (error) {
      console.error('Email service error:', error);
      throw error;
    }
  }

  /**
   * Send multiple emails in batch
   */
  static async sendBatch(emails: EmailData[]): Promise<void> {
    const promises = emails.map(email => this.send(email));
    await Promise.allSettled(promises);
  }

  /**
   * Get user email preferences
   */
  static async getPreferences(userId: string): Promise<EmailPreferences> {
    const { data, error } = await supabase
      .from('email_preferences')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Failed to get email preferences:', error);
      throw error;
    }

    // Return default preferences if none exist, handling nullable database values
    return data ? {
      marketing_emails: data.marketing_emails ?? true,
      activity_digest: data.activity_digest ?? true,
      project_notifications: data.project_notifications ?? true,
      usage_alerts: data.usage_alerts ?? true,
    } : {
      marketing_emails: true,
      activity_digest: true,
      project_notifications: true,
      usage_alerts: true,
    };
  }

  /**
   * Update user email preferences
   */
  static async updatePreferences(
    userId: string,
    preferences: Partial<EmailPreferences>
  ): Promise<void> {
    const { error } = await supabase
      .from('email_preferences')
      .upsert({
        user_id: userId,
        ...preferences,
        updated_at: new Date().toISOString(),
      } satisfies Database['public']['Tables']['email_preferences']['Insert']);

    if (error) {
      console.error('Failed to update email preferences:', error);
      throw error;
    }
  }

  /**
   * Check if user wants to receive a specific email type
   */
  static async canSendEmail(
    userId: string,
    template: EmailTemplate
  ): Promise<boolean> {
    // Always send critical emails
    const criticalEmails: EmailTemplate[] = [
      'payment-successful',
      'payment-failed',
      'subscription-cancelled',
      'team-invitation',
      'credit-purchase-receipt',
      'support-request',
    ];

    if (criticalEmails.includes(template)) {
      return true;
    }

    try {
      const preferences = await this.getPreferences(userId);

      // Map templates to preference categories
      const templatePreferences: Record<string, keyof EmailPreferences> = {
        'weekly-activity-digest': 'activity_digest',
        'project-shared': 'project_notifications',
        'analysis-complete': 'project_notifications',
        'project-deadline-reminder': 'project_notifications',
        'credits-running-low': 'usage_alerts',
        'credits-exhausted': 'usage_alerts',
        'storage-limit-warning': 'usage_alerts',
        'monthly-usage-report': 'usage_alerts',
      };

      const preferenceKey = templatePreferences[template];
      return preferenceKey ? preferences[preferenceKey] : true;
    } catch (error) {
      console.error('Error checking email preferences:', error);
      // Default to sending if we can't check preferences
      return true;
    }
  }

  /**
   * Get email history for a user
   */
  static async getEmailHistory(
    userId: string,
    limit = 50
  ): Promise<EmailLog[]> {
    const { data, error } = await supabase
      .from('email_logs')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Failed to get email history:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Generate unsubscribe token
   */
  static generateUnsubscribeToken(userId: string, emailType: string): string {
    const data = `${userId}:${emailType}:${Date.now()}`;
    return btoa(data);
  }

  /**
   * Verify unsubscribe token
   */
  static verifyUnsubscribeToken(token: string): {
    userId: string;
    emailType: string;
    timestamp: number;
  } | null {
    try {
      const decoded = atob(token);
      const [userId, emailType, timestamp] = decoded.split(':');

      // Token expires after 30 days
      const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
      if (parseInt(timestamp) < thirtyDaysAgo) {
        return null;
      }

      return {
        userId,
        emailType,
        timestamp: parseInt(timestamp),
      };
    } catch (error) {
      console.error('Invalid unsubscribe token:', error);
      return null;
    }
  }

  /**
   * Sync user to Resend marketing audience
   */
  static async syncToResend(
    action: 'add' | 'remove' | 'update',
    email: string,
    firstName?: string,
    lastName?: string,
    userId?: string
  ): Promise<void> {
    try {
      console.log(`🔄 Syncing contact to Resend: ${action} ${email}`);

      const { data, error } = await supabase.functions.invoke('manage-resend-contact', {
        body: {
          action,
          email,
          firstName,
          lastName,
          userId
        }
      });

      if (error) {
        console.error('❌ Failed to sync contact to Resend:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        throw new Error(`Failed to sync contact to Resend: ${error.message || JSON.stringify(error)}`);
      }

      console.log(`✅ Successfully ${action}ed contact ${email} to/from Resend`, data);
    } catch (error) {
      console.error('❌ Resend sync error:', error);
      throw error;
    }
  }

  /**
   * Add user to Resend marketing audience
   */
  static async addToResend(email: string, firstName?: string, lastName?: string, userId?: string): Promise<void> {
    return this.syncToResend('add', email, firstName, lastName, userId);
  }

  /**
   * Remove user from Resend marketing audience
   */
  static async removeFromResend(email: string, userId?: string): Promise<void> {
    return this.syncToResend('remove', email, undefined, undefined, userId);
  }

  /**
   * Update user contact in Resend marketing audience
   */
  static async updateInResend(email: string, firstName?: string, lastName?: string): Promise<void> {
    return this.syncToResend('update', email, firstName, lastName);
  }
}